<template>
  <div v-if="board && project && (projectMembers.length > 0)">
    <v-speed-dial
      v-model="fab"
      :bottom="true"
      :right="true"
      direction="top"
      :open-on-hover="true"
      transition='slide-y-reverse-transition'
      fixed
      >
      <v-btn
        slot="activator"
        v-model="fab"
        color="#0155a8"
        dark
        fab
        fixed
        >
        <v-icon>add</v-icon>
        <v-icon>close</v-icon>
      </v-btn>

      <v-tooltip bottom color="#fff" v-if="permit.canCreateTicket(board)">
        <v-btn fab
          color="#3a9cfc"
          small
          slot="activator"
          @click="newTicket = !newTicket"
        >
          <v-icon small color="white">local_activity</v-icon>
        </v-btn>
        <span class="black--text">Create new ticket</span>
      </v-tooltip>

      <v-tooltip bottom color="#fff" v-if="permit.canEditBoard(board)">
        <v-btn fab
          color="#FFD600"
          small
          slot="activator"
          @click="editBoard = !editBoard"
        >
          <v-icon small color="white">edit</v-icon>
        </v-btn>
        <span class="black--text">Edit board</span>
      </v-tooltip>

      <v-tooltip
        bottom
        color="#fff"
        >
        <v-btn v-if="watch"
          fab small
          color="#0155a8"
          slot="activator"
          @click="unsetWatch()"
          >
          <v-icon small color="white">visibility_off</v-icon>
        </v-btn>
        <v-btn v-else
          fab small
          color="#0155a8"
          slot="activator"
          @click="setWatch()"
          >
          <v-icon small color="white">visibility</v-icon>
        </v-btn>
        <span class="black--text">{{watch ? 'Ignore' : 'Watch'}}</span>
      </v-tooltip>
    </v-speed-dial>

    <v-card class="mb-3 sticky-element" :class="stickyClass">
      <v-layout row wrap justify-space-between >
        <v-flex xs12 sm6 class="pa-2">
          <v-layout row wrap>
            <v-flex shrink class="text-xs-center mr-3">
              <v-avatar size=48 >
                <img :src="logoThumbUrl" alt="Avatar" v-if="logoThumbUrl">
                <v-icon color="primary" large v-else>view_carousel</v-icon>
              </v-avatar>
            </v-flex>
            <v-flex shrink class="grey--text" >
              <h1 class="grey--text text--darken-2">
                {{board.attributes.name}}
              </h1>
              <h3 v-if="board.attributes.due_on">due {{board.attributes.due_on|formatDateAgo}} on {{board.attributes.due_on|formatDateLong}}</h3>
              <h3 v-else>no due date set</h3>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex shrink class="mr-3" xs6 md4 lg3>
          <v-text-field v-model="search"
            append-icon="search"
            label="Search this board"
            single-line
            hide-details
            inline-flex
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-card>

    <v-container fluid grid-list-md pa-0>
      <v-layout row>
        <v-flex v-for="(status, idx) in statuses" :key="`color-${idx}`" class="pl-1 lane">
            <h4 class="text-subheading text-uppercase status-header" :style="{'border-color': status.attributes.color }">
              {{ status.attributes.name }}
            </h4>
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex v-for="(status, idx) in statuses" :key="`content-${idx}`" class="pl-1 lane">
          <draggable
            class="layout row wrap list-group"
            :list="groupedTickets[status.id]"
            group="tickets"
            @end="dragEnd"
            :data-status-id="status.id"
            :disabled="!canDragCard"
            >
            <v-flex xs12 v-for="tck in groupedTickets[status.id]" :key="tck.id"
              :data-ticket-id="tck.id"
              :data-ticket-tag-list="tck.attributes.tag_list"
              >
              <ticketCard :ticket="tck" :membership="membershipForTicket(tck)" :status="status" :board="board"></ticketCard>
            </v-flex>
          </draggable>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog
      v-if="project && board && (projectMembers.length > 0) && (statuses.length > 0)"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="newTicket"
      max-width="65%"
      persistent
      >
      <!--
        When we add a new ticket to a board, we should only be able to use statuses used by that board
        Otherwise the ticket won't show up on the board
      -->
      <ticketNew :project="project" :boards="[board]" :members="projectMembers" :statuses="statuses"></ticketNew>
    </v-dialog>

    <v-dialog
      v-if="project && board && (projectStatuses.length > 0)"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="editBoard"
      max-width="500px"
      persistent
      >
      <boardEdit :project="project" :board="board" :statuses="projectStatuses"></boardEdit>
    </v-dialog>
  </div>
</template>

<script>
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'
  import permissions from '@/utilities/permissions'

  import draggable from 'vuedraggable'

  import projectService from '@/services/project'
  import memberService from '@/services/member'
  import boardService from '@/services/board'
  import placementService from '@/services/placement'
  import statusService from '@/services/status'
  import ticketService from '@/services/ticket'
  import commentService from '@/services/comment'
  import watchService from '@/services/watch'

  import ticketCard from '@/components/board/_ticketCard'
  import ticketNew from '@/components/ticket/_new'
  import boardEdit from '@/components/board/_edit'

  export default {
    name: 'board-show',

    components: {
      ticketCard: ticketCard,
      ticketNew: ticketNew,
      boardEdit: boardEdit,
      draggable: draggable
    },

    // to get to a board you must know the project
    props: ['id'],

    data () {
      return {
        auth: authentication,
        permit: permissions,
        board: null,
        project: null,
        tickets: [],
        statuses: [],
        projectStatuses: [],
        projectMembers: [],
        boardPlacements: [],

        // dialogs
        newTicket: false,
        editBoard: false,

        search: null,
        fab: false,
        watch: null
      }
    },

    mounted () {
      EventBus.$on('board-updated', (payload) => {
        // close the dialog
        this.editBoard = false
        // update our local copy
        if (payload) {
          this.board = Object.assign({}, payload)
        }
      })

      EventBus.$on('ticket-created', (payload) => {
        // close the dialog and insert the ticket
        this.newTicket = false
        this.loadBoardPlacements()
        this.loadBoardTickets()
      })

      this.loadBoard()
    },

    computed: {
      stickyClass () {
        if (window.innerWidth > 599) {
          if (this.scroll > 0) {
            return 'elevation-8'
          } else {
            return ''
          }
        }
      },

      logoThumbUrl () {
        if (this.project.links && this.project.links.logo_thumb_uri) {
          return (process.env.API_URL + this.project.links.logo_thumb_uri)
        }
        return null
      },

      searchedTickets () {
        if (this.search) {
          return this.tickets.filter((tck) => {
            // number
            // title
            return String(tck.attributes.sequential_id).includes(this.search) || tck.attributes.title.toLowerCase().includes(this.search)
          })
        } else {
          // nothing in the saerch field
          return this.tickets
        }
      },

      sortedTickets () {
        // boardPlacements are already sorted by position, so we can just mimic it's order
        return this.searchedTickets.sort((a, b) => {
          let aIdx = this.boardPlacements.findIndex(pl => parseInt(pl.relationships.ticket.data.id) === parseInt(a.id))
          let bIdx = this.boardPlacements.findIndex(pl => parseInt(pl.relationships.ticket.data.id) === parseInt(b.id))
          return aIdx - bIdx
        })
      },

      groupedTickets () {
        let group = {}
        if (this.tickets) {
          // reserve a space for each status to keep it's list of tickets
          this.statuses.forEach((st) => {
            group[st.id] = []
          })

          this.sortedTickets.forEach((tck) => {
            if (group[tck.relationships.status.data.id]) {
              group[tck.relationships.status.data.id].push(tck)
            }
          })
        }
        return group
      },

      canDragCard () {
        if (this.projectMembers.length > 0) {
          // get our role in this project
          let thisMembership = this.projectMembers.find(mem => parseInt(mem.relationships.user.data.id) == this.auth.instance().id)
          // allowed   'regular', 'administrator', 'owner'
          // not allowed  'restricted'
          return (thisMembership && !(thisMembership.role === 'restricted'))
        }
        return false
      }
    },

    methods: {
      loadBoard () {
        boardService.show(
          (data) => {
            this.board = Object.assign({}, data)
            document.title = `Board - ${this.board.attributes.name}`

            this.loadBoardStatuses()
            this.loadBoardPlacements()
            this.loadBoardTickets()
            this.loadBoardWatches()
            this.loadProject()
          },
          (err) => {
            console.log(err)
          },
          this.id
        )
      },

      loadBoardStatuses () {
        this.statuses = []
        statusService.boardIndex(
          (data) => {
            this.statuses = data.sort((a, b) => { return parseInt(a.attributes.position) - parseInt(b.attributes.position) })
          },
          (err) => {
            console.log(err)
          },
          this.id
        )
      },

      loadBoardPlacements () {
        this.boardPlacements = []
        placementService.boardIndex(
          (data) => {
            this.boardPlacements = data.sort((a, b) => { return parseInt(a.attributes.position) - parseInt(b.attributes.position) })
          },
          (err) => {
            console.log(err)
          },
          this.id
        )
      },

      loadBoardWatches () {
        watchService.boardIndex(
          (data) => {
            this.watches = data
            this.watch = data.find(w => this.auth.isCurrentUser(w.relationships.user.data.id) && (w.relationships.watchable.data.type === 'board'))
          },
          (err) => {
            console.log(err)
          },
          this.board.id
        )
      },

      loadBoardTickets () {
        this.tickets = []
        ticketService.boardIndex(
          (data) => {
            this.tickets = data
          },
          (err) => {
            console.log(err)
          },
          this.id
        )
      },

      loadProject () {
        projectService.show(
          (data) => {
            this.project = Object.assign({}, data)
            this.loadProjectStatuses()
            this.loadProjectMembers()
          },
          (err) => {
            console.log(err)
          },
          this.board.relationships.project.data.id
        )
      },

      loadProjectStatuses () {
        this.projectStatuses = []
        statusService.projectIndex(
          (data) => {
            this.projectStatuses = data.sort((a, b) => { return parseInt(a.attributes.position) - parseInt(b.attributes.position) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      loadProjectMembers () {
        memberService.projectIndex(
          (data) => {
            this.projectMembers = data.filter(m => !m.attributes.discarded_at).sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
          },
          (err) => {
            console.log(err)
          },
          this.project.id
        )
      },

      reloadTicket (ticketId) {
        let ticketIdx = this.tickets.findIndex(tck => parseInt(tck.id) === parseInt(ticketId))
        return ticketService.show(
          (data) => {
            this.tickets[ticketIdx] = Object.assign({}, data)
          },
          (err) => {
            console.log(err)
          },
          ticketId
        )
      },

      membershipForTicket (ticket) {
        if (ticket.relationships.assignee.data) {
          let aid = parseInt(ticket.relationships.assignee.data.id)
          return this.projectMembers.find(mem => parseInt(mem.relationships.user.data.id) == aid)
        }
        return null
      },

      async dragEnd (drag) {
        // find the ticket we are dragging
        let ticketId = drag.item.dataset.ticketId
        let ticket = this.tickets.find(tck => parseInt(tck.id) == parseInt(ticketId))
        // only allow us to updat ethings if we have perms
        if (!this.permit.canEditTicket(ticket)) {
          return false
        }

        let newStatusId = drag.to.dataset.statusId
        let ticketTagList = drag.item.dataset.ticketTagList

        let newOrder = drag.newIndex
        // we have to offset the position with the number of tickets on the board BEFORE this one
        // so we count all the tickets in all the columns which are "left" of this one
        let offset = 1
        this.statuses.some((k) => {
          if (parseInt(k.id) === parseInt(newStatusId)) {
            return true // cause 'some' iterator to stop
          } else {
            offset = offset + this.groupedTickets[k.id].length
          }
        })

        // if the status changed, make a comment
        let oldStatusId = ticket.relationships.status.data.id
        if (parseInt(newStatusId) !== parseInt(oldStatusId)) {
          await this.updateTicketStatus(ticketId, newStatusId, ticketTagList)
        }
        //  always update the placement, changing status implies an updated position
        this.updateTicketPlacement(ticketId, newOrder + offset)
      },

      updateTicketStatus (ticketId, statusId, ticketTagList) {
        return commentService.ticketCreate(
          (data) => {
            // nothing to do here ?
          },
          (err) => {
            console.log(err)
          },
          ticketId,
          {
            status_id: statusId,
            tag_list: ticketTagList.split(',')
            // NOTE: when we drag a ticket, we post a new comment and we need to preserve the tags
            // the tickets most recent tags are conveniently returned with the ticket
            // itself, even though they reside on the comments
          }
        )
      },

      updateTicketPlacement (ticketId, position) {
        let placementIdx = this.boardPlacements.findIndex(pl => parseInt(pl.relationships.ticket.data.id) === parseInt(ticketId))

        return placementService.ticketUpdate(
          async (data) => {
            await this.reloadTicket(ticketId)
            this.boardPlacements[placementIdx] = Object.assign({}, data)
            this.boardPlacements = this.boardPlacements.sort(
              (a, b) => {
                return parseInt(a.attributes.position) - parseInt(b.attributes.position)
              }
            )
          },
          (err) => {
            console.log(err)
          },
          ticketId,
          {
            id: this.boardPlacements[placementIdx].id,
            position: position
          }
        )
      },

      setWatch () {
        watchService.boardCreate(
          (data) => {
            this.watch = data
            this.watches.push(data)
          },
          (err) => {
            console.log(err)
          },
          this.board.id,
          {user_id: this.auth.userId()}
        )
      },

      unsetWatch () {
        watchService.boardDelete(
          (data) => {
            this.watches = this.watches.filter(w => w.id !== this.watch.id)
            this.watch = null
          },
          (err) => {
            console.log(err)
          },
          this.board.id,
          this.watch.id
        )
      }
    }
  }
</script>

<style lang="scss" scoped>
  .v-speed-dial{
    bottom: 110px!important;
    right: 72px;

    .v-speed-dial__list .v-tooltip {
      display: block;
      margin: 0 auto;
    }
  }

  .status-header {
    border-bottom: 10px solid;
  }

  @media screen and (min-width: 600px) {
    .sticky-element{
      position: sticky;
      position: -webkit-sticky;
      top: 65px;
      z-index: 5;
    }
  }

  @media screen and (min-width: 0px) {
    .lane {
      min-width: 100%;
      max-width: 100%;
    }
  }
  @media screen and (min-width: 600px) {
    .lane {
      min-width: 50%;
      max-width: 50%;
    }
  }
  @media screen and (min-width: 960px) {
    .lane {
      min-width: 33.333%;
      max-width: 33.333%;
    }
  }
  @media screen and (min-width: 1264px) {
    .lane {
      min-width: 25%;
      max-width: 25%;
    }
  }
  @media screen and (min-width: 1904px) {
    .lane {
      min-width: 15%;
      max-width: 15%;
    }
  }

  .grid-list-md {
    overflow-x: scroll;
    overflow-y: hidden;
  }

</style>



// WEBPACK FOOTER //
// src/components/board/show.vue